import { useCallback, useEffect, useRef, type RefObject } from 'react';
import { useFocusableElements } from './useFocusableElements';
import { useModalStackStore } from '@/stores';

function isInsideAllowedOutsideTarget(target: HTMLElement, selectors: readonly string[]): boolean {
    if (!selectors.length) {
        return false;
    }
    return selectors.some((selector) => target.closest(selector) !== null);
}

export function useFocusTrap(
    ref: RefObject<HTMLElement | null>,
    isOpen: boolean,
    onClose: () => void,
    isLoading: boolean,
    modalId?: string,
    preserveFocus: boolean = false,
    blockOutsideInteraction: boolean = true,
    allowedOutsideSelectors: readonly string[] = []
) {
    const getFocusableElements = useFocusableElements(ref);
    const prevActiveElement = useRef<HTMLElement | null>(null);
    const hasInitialFocused = useRef<boolean>(false);
    const lastFocusedElement = useRef<HTMLElement | null>(null);
    const preserveFocusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { isTopModal, modalStack } = useModalStackStore();

    // Function to get modal region elements sorted by order
    const getModalRegions = useCallback((): HTMLElement[] => {
        if (!ref.current) return [];

        const regions = ref.current.querySelectorAll('[data-modal-region="true"]');
        const visibleRegions = Array.from(regions).filter((el) => {
            const htmlEl = el as HTMLElement;
            return !!(htmlEl.offsetWidth || htmlEl.offsetHeight || htmlEl.getClientRects().length);
        }) as HTMLElement[];

        // Sort by data-modal-region-order if available, otherwise by DOM order
        return visibleRegions.sort((a, b) => {
            const orderA = parseInt(a.getAttribute('data-modal-region-order') || '0', 10);
            const orderB = parseInt(b.getAttribute('data-modal-region-order') || '0', 10);

            if (orderA !== orderB) {
                return orderA - orderB;
            }

            // Fallback to DOM order if no order attribute or same order
            const position = a.compareDocumentPosition(b);
            return position & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
        });
    }, [ref]);

    // Function to get current region context
    const getCurrentRegionContext = useCallback(
        (
            focusedElement: HTMLElement
        ): {
            isInRegion: boolean;
            regionIndex: number;
            availableRegions: HTMLElement[];
            hasModalRegionNav: boolean;
        } => {
            const availableRegions = getModalRegions();
            let isInRegion = false;
            let regionIndex = -1;
            const hasModalRegionNav = focusedElement.getAttribute('data-modal-region') === 'true';

            // Check if focused element is a region or inside a region
            for (let i = 0; i < availableRegions.length; i++) {
                if (availableRegions[i] === focusedElement || availableRegions[i].contains(focusedElement)) {
                    isInRegion = true;
                    regionIndex = i;
                    break;
                }
            }

            return { isInRegion, regionIndex, availableRegions, hasModalRegionNav };
        },
        [getModalRegions]
    );

    // Function to find nearest region before or after current element
    const findNearestRegion = useCallback(
        (currentElement: HTMLElement, availableRegions: HTMLElement[], direction: 'up' | 'down'): HTMLElement | null => {
            if (availableRegions.length === 0 || !ref.current) return null;

            // Get all visible elements in the modal
            const allElements = Array.from(ref.current.querySelectorAll('*')).filter((el) => {
                const htmlEl = el as HTMLElement;
                return !!(htmlEl.offsetWidth || htmlEl.offsetHeight || htmlEl.getClientRects().length);
            }) as HTMLElement[];

            const currentIndex = allElements.indexOf(currentElement);
            if (currentIndex === -1) return null;

            // Map regions to their positions in DOM
            const sortedRegions = availableRegions
                .map((region) => ({
                    element: region,
                    index: allElements.indexOf(region),
                }))
                .filter((item) => item.index !== -1)
                .sort((a, b) => a.index - b.index);

            if (direction === 'down') {
                // Find first region after current element
                const nextRegion = sortedRegions.find((region) => region.index > currentIndex);
                // If no region after, wrap to first region
                return nextRegion ? nextRegion.element : sortedRegions.length > 0 ? sortedRegions[0].element : null;
            } else {
                // Find last region before current element
                const prevRegions = sortedRegions.filter((region) => region.index < currentIndex);
                // If no region before, wrap to last region
                if (prevRegions.length > 0 && prevRegions[prevRegions.length - 2]) {
                    return prevRegions[prevRegions.length - 2].element;
                } else {
                    return sortedRegions.length > 0 ? sortedRegions[sortedRegions.length - 1].element : null;
                }
            }
        },
        [ref]
    );

    const focusModalRegion = useCallback(
        (region: HTMLElement) => {
            if (region.getAttribute('tabindex') === '-1') {
                const focusableSelector = [
                    'button:not([disabled]):not([tabindex="-1"])',
                    'input:not([disabled])',
                    'textarea:not([disabled])',
                    'select:not([disabled])',
                    'a[href]',
                    '[tabindex]:not([tabindex="-1"])',
                ].join(', ');
                const firstFocusable = region.querySelector(focusableSelector) as HTMLElement | null;
                if (firstFocusable) {
                    firstFocusable.focus();
                    return;
                }
            }
            region.focus();
        },
        []
    );

    const resolveNextFocusIndex = useCallback(
        (focusable: HTMLElement[], activeElement: HTMLElement, step: number): number => {
            const currentIndex = focusable.indexOf(activeElement);
            if (currentIndex !== -1) {
                return (currentIndex + step + focusable.length) % focusable.length;
            }

            if (!ref.current?.contains(activeElement)) {
                return step === 1 ? 0 : focusable.length - 1;
            }

            if (step === 1) {
                for (let i = 0; i < focusable.length; i++) {
                    const el = focusable[i];
                    if (
                        el === activeElement ||
                        (activeElement.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0
                    ) {
                        return i;
                    }
                }
                return 0;
            }

            for (let i = focusable.length - 1; i >= 0; i--) {
                const el = focusable[i];
                if (
                    el === activeElement ||
                    (activeElement.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING) !== 0
                ) {
                    return i;
                }
            }
            return focusable.length - 1;
        },
        [ref]
    );

    // Track focus changes if preserveFocus is enabled
    useEffect(() => {
        if (!preserveFocus || !isOpen || isLoading) {
            return;
        }

        const handleFocusChange = (e: FocusEvent) => {
            if (ref.current && e.target) {
                const targetElement = e.target as HTMLElement;
                // Only save if focus is within this modal/component
                if (ref.current.contains(targetElement)) {
                    lastFocusedElement.current = targetElement;
                }
            }
        };

        document.addEventListener('focusin', handleFocusChange);
        return () => {
            document.removeEventListener('focusin', handleFocusChange);
        };
    }, [preserveFocus, isOpen, isLoading, ref]);

    useEffect(() => {
        if (!isOpen || isLoading) return;

        const handleFocusIn = (e: FocusEvent) => {
            if (modalId && !isTopModal(modalId)) return;
            if (!ref.current) return;

            const target = e.target as HTMLElement;
            if (ref.current.contains(target)) return;
            if (isInsideAllowedOutsideTarget(target, allowedOutsideSelectors)) return;

            e.stopImmediatePropagation();
            const focusable = getFocusableElements();
            if (focusable.length > 0) {
                focusable[0].focus();
            } else {
                ref.current?.focus();
            }
        };

        const handlePointerDown = (e: Event) => {
            if (modalId && !isTopModal(modalId)) return;
            if (!ref.current) return;

            const target = e.target as HTMLElement;
            const modalOverlay = ref.current.closest('[role="dialog"]')?.parentElement;
            if (ref.current.contains(target)) return;
            if (modalOverlay && modalOverlay.contains(target)) return;
            if (isInsideAllowedOutsideTarget(target, allowedOutsideSelectors)) return;

            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
        };

        if (blockOutsideInteraction) {
            document.addEventListener('focusin', handleFocusIn, true);
            document.addEventListener('mousedown', handlePointerDown, true);
            document.addEventListener('click', handlePointerDown, true);
            document.addEventListener('touchstart', handlePointerDown, { capture: true, passive: false });
            document.addEventListener('touchend', handlePointerDown, { capture: true, passive: false });
        }

        return () => {
            if (blockOutsideInteraction) {
                document.removeEventListener('focusin', handleFocusIn, true);
                document.removeEventListener('mousedown', handlePointerDown, true);
                document.removeEventListener('click', handlePointerDown, true);
                document.removeEventListener('touchstart', handlePointerDown, true);
                document.removeEventListener('touchend', handlePointerDown, true);
            }
        };
    }, [isOpen, isLoading, ref, modalId, isTopModal, getFocusableElements, modalStack, blockOutsideInteraction, allowedOutsideSelectors]);

    // Track if this modal was top modal in the previous render
    const wasTopModal = useRef<boolean>(false);

    useEffect(() => {
        if (!isOpen || isLoading) {
            // Reset the flag when modal is closed or loading
            if (!isOpen) {
                hasInitialFocused.current = false;
                wasTopModal.current = false;
                if (preserveFocus) {
                    lastFocusedElement.current = null;
                }
            }
            return;
        }

        const isCurrentlyTopModal = modalId ? isTopModal(modalId) : true;

        // Check if this modal just became the top modal (wasn't before, but is now)
        const justBecameTopModal = !wasTopModal.current && isCurrentlyTopModal;

        if (!isCurrentlyTopModal) {
            wasTopModal.current = false;
            return;
        }

        // Update the tracking ref
        wasTopModal.current = true;

        // Store the previous active element only if it's not already stored
        if (!prevActiveElement.current) {
            prevActiveElement.current = document.activeElement as HTMLElement;
        }

        // Focus on initial render OR when this modal just became the top modal
        if (!hasInitialFocused.current || justBecameTopModal) {
            const focusable = getFocusableElements();
            // Focus on the first focusable element of the current modal
            if (focusable.length > 0 && document.activeElement !== focusable[0]) {
                setTimeout(() => {
                    focusable[0].focus();
                }, 0);
            } else if (ref.current && document.activeElement !== ref.current) {
                setTimeout(() => {
                    ref.current?.focus();
                }, 0);
            }

            // Mark that initial focus has been set
            hasInitialFocused.current = true;
        } else if (preserveFocus) {
            // Restore focus after re-render when preserveFocus is enabled
            if (preserveFocusTimerRef.current !== null) {
                clearTimeout(preserveFocusTimerRef.current);
                preserveFocusTimerRef.current = null;
            }
            if (lastFocusedElement.current) {
                const lastElement = lastFocusedElement.current;
                if (document.body.contains(lastElement) && ref.current?.contains(lastElement)) {
                    preserveFocusTimerRef.current = setTimeout(() => {
                        preserveFocusTimerRef.current = null;
                        lastElement.focus();
                    }, 50);
                } else {
                    const focusable = getFocusableElements();
                    if (focusable.length > 0) {
                        preserveFocusTimerRef.current = setTimeout(() => {
                            preserveFocusTimerRef.current = null;
                            focusable[0].focus();
                        }, 50);
                    }
                }
            } else {
                const focusable = getFocusableElements();
                if (focusable.length > 0) {
                    preserveFocusTimerRef.current = setTimeout(() => {
                        preserveFocusTimerRef.current = null;
                        focusable[0].focus();
                    }, 50);
                }
            }
        }

        const handleKeyDown = (e: KeyboardEvent) => {
            // Only handle keyboard events if this modal is the top modal
            if (modalId && !isTopModal(modalId)) {
                return;
            }

            const activeElement = document.activeElement as HTMLElement;
            const isFocusInside = ref.current?.contains(activeElement) ?? false;
            const isInAllowedOutside = isInsideAllowedOutsideTarget(activeElement, allowedOutsideSelectors);

            if (e.key === 'Escape') {
                if (!isInAllowedOutside) {
                    e.preventDefault();
                    onClose();
                }
                return;
            }

            if (!isFocusInside && (!blockOutsideInteraction || isInAllowedOutside)) {
                return;
            }

            // Handle ArrowUp and ArrowDown for modal region navigation
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                const focusedElement = document.activeElement as HTMLElement;
                const context = getCurrentRegionContext(focusedElement);

                // Only handle if there are regions available
                if (context.availableRegions.length > 0) {
                    e.preventDefault();

                    if (e.key === 'ArrowUp') {
                        if (context.hasModalRegionNav && context.isInRegion) {
                            // Currently in a region, move to previous region
                            const prevIndex = context.regionIndex - 1;
                            if (prevIndex >= 0) {
                                focusModalRegion(context.availableRegions[prevIndex]);
                            } else {
                                // At first region, wrap to last region
                                focusModalRegion(context.availableRegions[context.availableRegions.length - 1]);
                            }
                        } else {
                            // Not in a region, find nearest region before current element (with wrap)
                            const nearestRegion = findNearestRegion(focusedElement, context.availableRegions, 'up');
                            if (nearestRegion) {
                                focusModalRegion(nearestRegion);
                            }
                        }
                    } else {
                        // ArrowDown
                        if (context.hasModalRegionNav && context.isInRegion) {
                            // Currently in a region, move to next region
                            const nextIndex = context.regionIndex + 1;
                            if (nextIndex < context.availableRegions.length) {
                                focusModalRegion(context.availableRegions[nextIndex]);
                            } else {
                                // At last region, wrap to first region
                                focusModalRegion(context.availableRegions[0]);
                            }
                        } else {
                            // Not in a region, find nearest region after current element (with wrap)
                            const nearestRegion = findNearestRegion(focusedElement, context.availableRegions, 'down');
                            if (nearestRegion) {
                                focusModalRegion(nearestRegion);
                            }
                        }
                    }
                    return;
                }
            }

            if (e.key === 'Tab' || e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                const focusable = getFocusableElements();
                if (!focusable.length) return;

                const step = e.key == 'ArrowLeft' ? -1 : 1;
                const nextIndex = resolveNextFocusIndex(focusable, activeElement, step);

                e.preventDefault();
                focusable[nextIndex]?.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            if (preserveFocusTimerRef.current !== null) {
                clearTimeout(preserveFocusTimerRef.current);
                preserveFocusTimerRef.current = null;
            }
        };
    }, [
        isOpen,
        getFocusableElements,
        onClose,
        ref,
        modalId,
        isTopModal,
        modalStack,
        getModalRegions,
        getCurrentRegionContext,
        findNearestRegion,
        focusModalRegion,
        resolveNextFocusIndex,
        isLoading,
        preserveFocus,
        blockOutsideInteraction,
        allowedOutsideSelectors,
    ]);
}
